import { graphql, type Permissions$data, type PermissionLevel$options } from '$houdini'
import sessionStore from 'stores/session'
import type { ClientSession } from 'stores/session'

/**
 * Shared fragment for session-level permissions.
 * Used by both the legacy login mutation and the OAuth session query.
 */
graphql(`
	fragment Permissions on Session {
		permissions {
			CONFIGURATION_CAN_CONFIGURE_STORES
			CUSTOMERS_CAN_ADD
			CUSTOMERS_CAN_EDIT_ADDRESSES
			CUSTOMERS_CAN_EDIT_ADVANCED
			CUSTOMERS_CAN_EDIT_BILLING_ADDRESS
			CUSTOMERS_CAN_EDIT_CREDIT_LIMIT
			CUSTOMERS_CAN_EDIT_CUSTOMERS
			CUSTOMERS_CAN_EDIT_PRICING
			CUSTOMERS_CAN_EDIT_SALESMAN_STORE
			CUSTOMERS_CAN_EDIT_TERMS
			CUSTOMERS_CAN_MANAGE_MESSAGES
			CUSTOMERS_CAN_MANAGE_OPTIONS
			CUSTOMERS_CAN_MANAGE_UNITS
			CUSTOMERS_CAN_MERGE
			DASHBOARD_CHART_EDIT
			DASHBOARD_CHART_EDIT_QUERY
			DASHBOARD_REPORT_EDIT_ALL
			DASHBOARD_REPORT_EDIT_OWN
			DASHBOARD_REPORT_VIEW
			GLOBAL_USER_ADMINISTRATOR
			INTERCHANGE_CAN_OPEN_SCREEN
			PART_CONFIGURATION_CAN_EDIT_PART_TYPES
			PARTS_CAN_CLONE_PARTS
			PARTS_CAN_CREATE_CONDITIONS
			PARTS_CAN_EDIT_ATTACHMENTS
			PARTS_CAN_EDIT_COST
			PARTS_CAN_EDIT_GLCATEGORY
			PARTS_CAN_EDIT_INFO
			PARTS_CAN_EDIT_LOCATION
			PARTS_CAN_EDIT_PRICES
			PARTS_CAN_EDIT_QUANTITY
			PARTS_CAN_EDIT_STATUS
			PARTS_CAN_EDIT_STOCKING_DATA
			PARTS_CAN_REPLICATE_PARTS
			PARTS_CAN_SAVE_PARTS
			PARTS_CAN_VIEW_COST
			PARTS_CAN_VIEW_PARTS
			PARTS_EDIT_RESTRICTED_PART_TYPE_PRICES
			PARTS_EDIT_RESTRICTED_PURCHASE_VENDOR
			PURCHASE_ORDERS_CAN_VIEW
			SALES_ORDERS_CAN_CHANGE_DATE
			SALES_ORDERS_CAN_EDIT
			SALES_ORDERS_CAN_VIEW
			SEARCH_CAN_SEARCH_VEHICLES
			SETTINGS_GROUP_ADMINISTRATOR
			TRANSFER_ORDERS_CAN_VIEW
			VEHICLES_CAN_CHANGE_STOCK_QUANTITY
			VEHICLES_CAN_EDIT_ADDITIONAL_INFORMATION
			VEHICLES_CAN_EDIT_ATTACHMENTS
			VEHICLES_CAN_EDIT_BASIC
			VEHICLES_CAN_EDIT_BIDS
			VEHICLES_CAN_EDIT_GL_CATEGORY
			VEHICLES_CAN_EDIT_NON_BID_UNITS
			VEHICLES_CAN_EDIT_QANDA
			VEHICLES_CAN_EDIT_STATUS
			VEHICLES_CAN_EDIT_STOCK_NUMBER
			VEHICLES_CAN_EDIT_WHOLE_UNIT_ADVANCED
			VEHICLES_CAN_EDIT_WHOLE_UNITS
			VEHICLES_CAN_VIEW_VEHICLE_PERFORMANCE
			VENDORS_CAN_EDIT_VENDORS
			VENDORS_CAN_MERGE
			WEB_CAN_LOGIN
			WORK_ORDERS_CAN_VIEW
		}
	}
`)

/**
 * Shared fragment for user account fields needed to populate the session store.
 * Used by both the legacy login mutation and the OAuth session query.
 */
graphql(`
	fragment SessionUserAccount on UserAccount {
		id
		username: name
		email: workEmail
		wageRateId
		deliveryZoneId
		glDepartmentId
		driver
		salesPerson
		worker
		firstName
		lastName
		recoveryEmail
		groups {
			id
			name
		}
		stores
		highestAccessPermissions {
			id
			codeName
			category
			description
			level
		}
		settings {
			preferences {
				optInToTestingFeatures
			}
		}
		authorizedStores {
			id
			name
			code
		}
	}
`)

type SessionUserAccountInput = {
	id: number
	authorizedStores: Array<{ id: number; name: string; code: string }> | null | undefined
	highestAccessPermissions: Array<{ codeName: string; level: PermissionLevel$options }> | null | undefined
	[key: string]: unknown
}

/**
 * Populates the session store from data common to both the legacy login mutation
 * and the OAuth cookie-based session query.
 *
 * @param sessionBase - Remaining session fields (spread directly into the store)
 * @param userAccount - User account data from the session
 * @param sessionPermissions - Store-level permission flags
 * @param companyId - companyInfo.id from the session
 * @param storeId - The store the user logged into
 * @param authToken - Actual token for legacy login, or 'bff-cookie' sentinel for OAuth
 */
export function applySessionToStore({
	sessionBase,
	userAccount,
	sessionPermissions,
	companyId,
	storeId,
	authToken,
}: {
	sessionBase: Record<string, unknown>
	userAccount: SessionUserAccountInput
	sessionPermissions: Permissions$data['permissions']
	companyId: number
	storeId: number
	authToken: string
}): void {
	sessionStore.set({
		...sessionBase,
		...userAccount,
		authToken,
		authorizedStores: userAccount.authorizedStores ?? [],
		userAccountId: userAccount.id,
		currentStore: storeId,
		sessionPermissions,
		userPermissions:
			userAccount.highestAccessPermissions?.reduce(
				(acc: Record<string, PermissionLevel$options>, { codeName, level }) => {
					acc[codeName] = level
					return acc
				},
				{},
			) ?? {},
		companyCode: companyId,
		licenseInfo: { limits: {}, toggles: {} },
	} as unknown as ClientSession)
}
